import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, Send, Calendar as CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PROJECT_TYPES } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", email: "", phone: "", project_type: "", location: "", desired_start_date: "", description: "" };

export default function QuoteForm({ defaultProjectType = "" }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...emptyForm, project_type: defaultProjectType });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name";
    if (!form.email.trim()) er.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email";
    if (!form.phone.trim()) er.phone = "Please enter your phone";
    if (!form.project_type) er.project_type = "Select a project type";
    if (!form.location.trim()) er.location = "Enter your town / location";
    if (!form.desired_start_date) er.desired_start_date = "Choose your desired start date";
    else {
      const selectedDate = new Date(form.desired_start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) er.desired_start_date = "Please select today or a future date";
    }
    if (!form.description.trim()) er.description = "Tell us a bit about your project";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      // Fire conversion events for Meta Pixel + Google Analytics
      if (typeof window !== "undefined") {
        if (window.fbq) window.fbq("track", "Lead");
        if (window.gtag) window.gtag("event", "generate_lead");
      }
      navigate("/thank-you");
    } catch (err) {
      toast.error("Something went wrong. Please call us or try again.");
      setLoading(false);
    }
  };

  const fieldCls = (key) =>
    `bg-white border-brand-border focus-visible:ring-brand-orange focus-visible:ring-2 ${errors[key] ? "border-red-400" : ""}`;

  return (
    <form data-testid="quote-form" onSubmit={onSubmit} className="space-y-5" noValidate>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="qf-name" className="text-brand-dark font-medium">Full Name *</Label>
          <Input id="qf-name" data-testid="quote-name" value={form.name} onChange={set("name")} placeholder="Jane Smith" className={`mt-2 ${fieldCls("name")}`} />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </div>
        <div>
          <Label htmlFor="qf-phone" className="text-brand-dark font-medium">Phone *</Label>
          <Input id="qf-phone" data-testid="quote-phone" value={form.phone} onChange={set("phone")} placeholder="(732) 555-0100" className={`mt-2 ${fieldCls("phone")}`} />
          {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="qf-email" className="text-brand-dark font-medium">Email *</Label>
          <Input id="qf-email" type="email" data-testid="quote-email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className={`mt-2 ${fieldCls("email")}`} />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="qf-location" className="text-brand-dark font-medium">Town / Location *</Label>
          <Input id="qf-location" data-testid="quote-location" value={form.location} onChange={set("location")} placeholder="Freehold, NJ" className={`mt-2 ${fieldCls("location")}`} />
          {errors.location && <p className="mt-1 text-xs text-red-500">{errors.location}</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="qf-start-date" className="text-brand-dark font-medium">Desired Starting Date *</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              id="qf-start-date"
              data-testid="quote-start-date"
              className={`mt-2 flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange ${form.desired_start_date ? "text-brand-dark" : "text-muted-foreground"} ${errors.desired_start_date ? "border-red-400" : "border-brand-border"}`}
            >
              {form.desired_start_date
                ? format(new Date(form.desired_start_date + "T00:00:00"), "MM/dd/yyyy")
                : "MM/DD/YYYY"}
              <CalendarIcon size={16} className="text-brand-orange shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={form.desired_start_date ? new Date(form.desired_start_date + "T00:00:00") : undefined}
              onSelect={(date) => {
                if (!date) return;
                setForm((f) => ({ ...f, desired_start_date: format(date, "yyyy-MM-dd") }));
                setErrors((er) => ({ ...er, desired_start_date: undefined }));
              }}
              disabled={(date) => {
                const t = new Date();
                t.setHours(0, 0, 0, 0);
                return date < t;
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {errors.desired_start_date && <p className="mt-1 text-xs text-red-500">{errors.desired_start_date}</p>}
      </div>

      <div>
        <Label className="text-brand-dark font-medium">Project Type *</Label>
        <Select value={form.project_type} onValueChange={set("project_type")}>
          <SelectTrigger data-testid="quote-project-type" className={`mt-2 ${fieldCls("project_type")}`}>
            <SelectValue placeholder="Select your project" />
          </SelectTrigger>
          <SelectContent>
            {PROJECT_TYPES.map((p) => (
              <SelectItem key={p} value={p} data-testid={`quote-project-option-${p.replace(/\s+/g, "-").toLowerCase()}`}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.project_type && <p className="mt-1 text-xs text-red-500">{errors.project_type}</p>}
      </div>

      <div>
        <Label htmlFor="qf-desc" className="text-brand-dark font-medium">Project Description *</Label>
        <Textarea id="qf-desc" data-testid="quote-description" value={form.description} onChange={set("description")} rows={5} placeholder="Tell us about your space, your goals, and anything that's been frustrating you…" className={`mt-2 resize-none ${fieldCls("description")}`} />
        {errors.description && <p className="mt-1 text-xs text-red-500">{errors.description}</p>}
      </div>

      <button type="submit" data-testid="quote-submit" disabled={loading} className="btn-cta w-full !py-4 text-base disabled:opacity-70 disabled:cursor-not-allowed">
        {loading ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : (<>Request My Free Quote <Send size={16} /></>)}
      </button>
      <p className="text-center text-xs text-brand-muted">No spam, ever. We typically reply the same day.</p>
    </form>
  );
}
